import type { DatabaseShardManifestEntry } from '@blocksuite/affine-model';
import {
  createWriterShardId,
  createWriterShardPrefix,
  databaseShardManifestEntryToYMap,
  normalizeDatabaseShardManifestEntry,
} from '@streamlain/database-identity';
import { type Doc as YDoc, Map as YMap } from 'yjs';

const FILE_INDEX_SHARD_REGISTRY_KEY_PREFIX =
  'streamlain:file-index-shard-registry:';

/**
 * Writer shards of a Files database by shard id. Each value is the CRDT form
 * of the entry, one field per value key and row fingerprint session: an entry
 * stored as one plain value would carry its fingerprint bytes inside that
 * value, which Yjs development builds refuse to freeze ("Cannot freeze array
 * buffer views with elements"), so the server rejects the publication.
 */
export type FileIndexShardRegistry = YMap<YMap<unknown>>;

export function getFileIndexShardRegistry(
  doc: YDoc,
  databaseId: string
): FileIndexShardRegistry {
  return doc.getMap<YMap<unknown>>(
    `${FILE_INDEX_SHARD_REGISTRY_KEY_PREFIX}${databaseId}`
  );
}

const registryReconcilers = new WeakMap<
  FileIndexShardRegistry,
  { readonly reconcilers: Set<() => void>; queued: boolean }
>();

/**
 * Calls one registered `reconcile` once after the changes of `registry` in a
 * task; returns its removal. Every open source of a Files database registers,
 * but its directory follows the registry once: each further reconcile would
 * publish the same manifest change again.
 */
export function observeFileIndexShardRegistry(
  registry: FileIndexShardRegistry,
  reconcile: () => void
): () => void {
  let state = registryReconcilers.get(registry);
  if (!state) {
    const created = { reconcilers: new Set<() => void>(), queued: false };
    registryReconcilers.set(registry, created);
    registry.observe(() => {
      if (created.queued) return;
      created.queued = true;
      queueMicrotask(() => {
        created.queued = false;
        created.reconcilers.values().next().value?.();
      });
    });
    state = created;
  }
  const { reconcilers } = state;
  const entry = () => reconcile();
  reconcilers.add(entry);
  return () => {
    reconcilers.delete(entry);
  };
}

/** Registered entries in canonical form, ordered by shard id. */
export function readFileIndexShardEntries(
  registry: FileIndexShardRegistry
): DatabaseShardManifestEntry[] {
  return sortFileIndexShardEntries(
    Array.from(registry.values(), stored =>
      normalizeDatabaseShardManifestEntry(stored)
    )
  );
}

export function readFileIndexShardEntry(
  registry: FileIndexShardRegistry,
  shardId: string
): DatabaseShardManifestEntry | undefined {
  const stored = registry.get(shardId);
  return stored === undefined
    ? undefined
    : normalizeDatabaseShardManifestEntry(stored);
}

export function writeFileIndexShardEntry(
  registry: FileIndexShardRegistry,
  entry: DatabaseShardManifestEntry
): void {
  registry.set(entry.shardId, databaseShardManifestEntryToYMap(entry));
}

export function getFileIndexWriterShardPrefix(doc: YDoc): string {
  return createWriterShardPrefix(doc.clientID);
}

export function nextFileIndexWriterShardId(
  doc: YDoc,
  _entries: readonly DatabaseShardManifestEntry[]
): string {
  return createWriterShardId(createWriterShardPrefix(doc.clientID));
}

export function sortFileIndexShardEntries(
  entries: Iterable<DatabaseShardManifestEntry>
): DatabaseShardManifestEntry[] {
  return Array.from(entries).sort((left, right) =>
    left.shardId.localeCompare(right.shardId)
  );
}

/** Canonical entries: registry values arrive in codec-dependent key order. */
export function projectFileIndexShardEntries(
  registryEntries: Iterable<DatabaseShardManifestEntry>,
  updatedEntries: readonly DatabaseShardManifestEntry[]
): DatabaseShardManifestEntry[] {
  const entries = new Map<string, DatabaseShardManifestEntry>();
  for (const entry of [...registryEntries, ...updatedEntries]) {
    const canonical = normalizeDatabaseShardManifestEntry(entry);
    entries.set(canonical.shardId, canonical);
  }
  return sortFileIndexShardEntries(entries.values());
}

/**
 * Whether the stored registry value holds `entry`, compared in canonical form:
 * stored field order depends on the writer and the CRDT codec.
 */
export function isSameFileIndexShardEntry(
  stored: unknown,
  entry: DatabaseShardManifestEntry
): boolean {
  return (
    stored !== undefined &&
    JSON.stringify(normalizeDatabaseShardManifestEntry(stored)) ===
      JSON.stringify(normalizeDatabaseShardManifestEntry(entry))
  );
}

export function selectFileIndexWriterShard(
  entries: readonly DatabaseShardManifestEntry[],
  writerPrefix: string,
  preferredShardId: string | undefined,
  targetBytes: number
): DatabaseShardManifestEntry | undefined {
  const preferred = preferredShardId
    ? entries.find(entry => entry.shardId === preferredShardId)
    : undefined;
  return (
    preferred ??
    entries.find(
      entry =>
        entry.shardId.startsWith(writerPrefix) &&
        (entry.rowCount === 0 || entry.encodedBytes < targetBytes)
    )
  );
}
